import { useEffect, useRef, type RefObject } from 'react';

import { paintToCanvas } from '../render/canvas';
import type { QrDrawModel } from '../render/model';

export interface QrCanvasProps {
  readonly model: QrDrawModel;
  readonly centerImage: CanvasImageSource | null;
  readonly moduleImage: CanvasImageSource | null;
  /** Exposed so the download button can read pixels from the same canvas. */
  readonly canvasRef: RefObject<HTMLCanvasElement | null>;
  readonly alt: string;
}

/**
 * Paints the draw model.
 *
 * All the branching that used to live here — finder-pattern detection, centre
 * cut-out, style dispatch across fifteen props — now happens in
 * `buildDrawModel`, which is pure and unit-tested. This component only owns
 * the DOM side: sizing the canvas for the device pixel ratio and repainting
 * when the model or the images change.
 */
export function QrCanvas({ model, centerImage, moduleImage, canvasRef, alt }: QrCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas === null) return;

    const paint = (): void => {
      paintToCanvas(canvas, model, { centerImage, moduleImage }, window.devicePixelRatio);
    };

    paint();

    // Repaint on DPR changes (e.g. dragging the window to another display).
    const media = window.matchMedia(`(resolution: ${String(window.devicePixelRatio)}dppx)`);
    media.addEventListener('change', paint);
    return () => {
      media.removeEventListener('change', paint);
    };
  }, [model, centerImage, moduleImage, canvasRef]);

  return (
    <div
      ref={containerRef}
      className="flex w-full items-center justify-center rounded-lg border bg-background p-4"
    >
      <canvas
        ref={canvasRef}
        role="img"
        aria-label={alt}
        className="h-auto max-w-full rounded"
        style={{ imageRendering: 'auto' }}
      />
    </div>
  );
}
